import {
  type Config,
  type LogLevel,
  type Sink,
  configure,
  configureSync,
  getConsoleSink,
  getLogger,
  getTextFormatter,
  jsonLinesFormatter,
} from '@logtape/logtape';
import { getPrettyFormatter } from '@logtape/pretty';
import { redactByField } from '@logtape/redaction';

import { environment } from './environment';
import { keys } from './keys';

const browser = typeof window !== 'undefined';

// Plain text in the browser: the console formatter's `%c` does not survive the TanStack relay.
// Both dev formatters omit the category while records have none.
// No word wrap: without a TTY (CI) it wraps at 80 columns and breaks the sync summary table.
function formatter() {
  if (browser) {
    return getTextFormatter({
      timestamp: 'time',
      format: ({ timestamp, level, category, message }) =>
        `${timestamp} [${level}] ${category ? `${category}: ` : ''}${message}`,
    });
  }
  return environment.production ? jsonLinesFormatter : getPrettyFormatter({ categoryWidth: 0, wordWrap: false });
}

const consoleSink = () => getConsoleSink({ formatter: formatter() });

let extraSinks: Record<string, () => Sink> = {};
// A server var throws on the client, and a client var needs `import.meta.env`, which throws under tsx.
let lowestLevel: LogLevel = (browser ? undefined : keys().LOG_LEVEL) ?? (environment.production ? 'warning' : 'debug');

export type ConfigureLoggingOptions = {
  /** Sink factories to route alongside the console, keyed by id. Merged with those already set. */
  sinks?: Record<string, () => Sink>;
  /** Overrides the level `LOG_LEVEL` selected. */
  level?: LogLevel;
};

function buildConfig({ sinks, level }: ConfigureLoggingOptions): Config<string, string> {
  if (sinks) extraSinks = { ...extraSinks, ...sinks };
  if (level !== undefined) lowestLevel = level;

  return {
    reset: true,
    // Every sink, since production stdout is collected too.
    sinks: Object.fromEntries(
      Object.entries({ console: consoleSink, ...extraSinks }).map(([id, sink]) => [id, redactByField(sink())]),
    ),
    loggers: [
      { category: [], lowestLevel, sinks: ['console', ...Object.keys(extraSinks)] },
      // LogTape's own diagnostics; below a warning it is noise.
      { category: ['logtape', 'meta'], lowestLevel: 'warning' },
    ],
  };
}

/** Applies the configuration. Async because `configureSync` rejects async-disposable sinks. */
export async function configureLogging(options: ConfigureLoggingOptions = {}): Promise<void> {
  await configure(buildConfig(options));
}

// Console only, so a logger works on import without an entry point having set it up.
configureSync(buildConfig({}));

export { getLogger };

/** Re-exported so an app can write a sink without taking its own dependency on LogTape. */
export type { LogLevel, LogRecord, Sink } from '@logtape/logtape';

/** Default logger instance. New code should prefer `getLogger([...])` with a category. */
export const logger = getLogger();
